import { useEffect, useRef } from 'react';

export interface UsePollingOptions {
  pollingFunction: () => Promise<void> | void;
  interval?: number;
  executeImmediately?: boolean;
  enabled?: boolean;
}

/**
 * Hook customizado para realizar polling de funções
 *
 * @param options - Configurações do polling
 * @returns Função para parar o polling manualmente
 */
export const usePolling = ({
  pollingFunction,
  interval = 5000,
  executeImmediately = true,
  enabled = true,
}: UsePollingOptions) => {
  const intervalRef = useRef<NodeJS.Timeout>();
  const isPollingActiveRef = useRef(false);

  const stopPolling = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = undefined;
      isPollingActiveRef.current = false;
    }
  };

  const startPolling = () => {
    if (isPollingActiveRef.current || !enabled) {
      return;
    }

    isPollingActiveRef.current = true;
    intervalRef.current = setInterval(pollingFunction, interval);
  };

  useEffect(() => {
    if (!enabled) {
      stopPolling();
      return;
    }

    if (executeImmediately) {
      pollingFunction();
    }

    startPolling();

    return () => {
      stopPolling();
    };
  }, [enabled, interval]);

  return stopPolling;
};
